import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, IconButton, Button } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CloudIcon from '@material-ui/icons/Cloud';
import { useApi, googleAuthApiRef } from '@backstage/core-plugin-api';

const BANNER_DISMISSED_KEY = 'gcp-banner-dismissed';

const useStyles = makeStyles((theme) => ({
  banner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.type === 'dark' ? '#1a3a5c' : '#e3f2fd',
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? '#2a4a6c' : '#bbdefb'}`,
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
  },
  icon: {
    color: theme.palette.type === 'dark' ? '#90caf9' : '#1565c0',
  },
  message: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  connectButton: {
    textTransform: 'none',
    fontWeight: 600,
    marginLeft: theme.spacing(2),
    whiteSpace: 'nowrap',
  },
  closeButton: {
    marginLeft: theme.spacing(1),
  },
}));

export const GcpConnectionBanner = () => {
  const classes = useStyles();
  const googleAuth = useApi(googleAuthApiRef);
  const [visible, setVisible] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Don't show if previously dismissed
    if (localStorage.getItem(BANNER_DISMISSED_KEY) === 'true') {
      setChecking(false);
      return;
    }

    // Check if Google is already connected
    let cancelled = false;
    googleAuth
      .getAccessToken(['openid', 'email'], { optional: true })
      .then((token) => {
        if (!cancelled) {
          setVisible(!token);
          setChecking(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVisible(true);
          setChecking(false);
        }
      });

    return () => { cancelled = true; };
  }, [googleAuth]);

  if (checking || !visible) return null;

  const handleConnect = async () => {
    try {
      await googleAuth.getAccessToken([
        'openid',
        'email',
        'profile',
        'https://www.googleapis.com/auth/cloud-platform.read-only',
      ]);
      setVisible(false);
    } catch {
      // User cancelled the popup - keep banner visible
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setVisible(false);
  };

  return (
    <Box className={classes.banner}>
      <Box className={classes.content}>
        <CloudIcon className={classes.icon} />
        <Typography className={classes.message}>
          Connect your Google account to access GCP projects, dashboards, and AI features.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          className={classes.connectButton}
          onClick={handleConnect}
        >
          Connect Google
        </Button>
      </Box>
      <IconButton
        size="small"
        className={classes.closeButton}
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
